import { Form, Input, Select } from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IHouseStepIdealTenant,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";
import { HouseAPI } from "../../../../../API/houseApi";
import {
  GenderValuesBE,
  HouseTenantCharacteristicsValues,
  HouseTenantDescriptionValues,
  ISelectItem,
  THouseTenantCharacteristics,
  THouseTenantDescription
} from "../../../../../interfaces/interfaces";
import { TLang } from "../../../../../assets/data/isoNameLanguage";
import { getLanguage } from "../../../../../utils/fetchMapInfo";
import { WizardButtonSelect } from "../../../components";
import {
  Sweeping,
  Sports,
  Conversation,
  Ecological,
  MountenClimbing,
  NoSmoking,
  Pets,
  ShakingHands,
  TeaCup,
  Vegan,
  VeganCheck,
  Religion
} from "components/Icons/Wizard/TenantPreferences/AllTenantPreferences";

import { StepViewOnlyValue } from "../../../Student/styles";
import { FormGroupOfTwoItems, RadioGroup } from "./styles";

const CHARACTERISTICS_ICONS: {
  [key in THouseTenantCharacteristics]: React.ReactNode;
} = {
  clean: <Sweeping />,
  quiet: <TeaCup />,
  eco_friendly: <Ecological />,
  animal_friendly: <Pets />,
  social: <Conversation />,
  pro_active: <Sports />,
  honest: <ShakingHands />,
  outgoing: <MountenClimbing />,
  religious: <Religion />
};
const DESCRIPTION_ICONS: {
  [key in THouseTenantDescription]: React.ReactNode;
} = {
  vegetarian: <Vegan />,
  no_smoking: <NoSmoking />,
  flexitarian: <VeganCheck />
};

const statePath = WIZARD_HOUSE_STEPS_PATHS.IdealTenant;
export const WizardStepIdealTenant: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepIdealTenant>>(
      state[statePath]
        ? {
            ...state[statePath],
            tenant_spoken_languages:
              Array.isArray(state[statePath]?.tenant_spoken_languages) &&
              state[statePath].tenant_spoken_languages.length > 0
                ? state[statePath].tenant_spoken_languages
                : ["ANY"]
          }
        : {}
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveIdealTenant(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveIdealTenantPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepIdealTenantView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

export const WizardStepIdealTenantView: TWizardStepComponentEditableView<IHouseStepIdealTenant> =
  React.memo(
    ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
      const { t, i18n } = useTranslation("house");
      const { t: th } = useTranslation("hostrooms");

      const inputSize = "large";
      const characteristicsOptions = HouseTenantCharacteristicsValues.map(
        (c) => ({
          value: c,
          label: t(
            `wizard.steps.IdealTenant.form.tenant_characteristics.options.${c}`
          ),
          icon: CHARACTERISTICS_ICONS[c]
        })
      );
      const descriptionOptions = HouseTenantDescriptionValues.map((c) => ({
        value: c,
        label: t(
          `wizard.steps.IdealTenant.form.tenant_description.options.${c}`
        ),
        icon: DESCRIPTION_ICONS[c]
      }));
      const genderOptions = GenderValuesBE.map((v) => ({
        value: v,
        label: t(`wizard.steps.IdealTenant.form.gender.options.${v}`)
      }));

      const currentLanguage: TLang = i18n.language.toUpperCase() as TLang;
      const allLanguagesOptions: ISelectItem[] = [
        {
          value: "ANY",
          label: th("languages.options.ANY")
        },
        ...getLanguage(currentLanguage)
      ];
      const isReadonly = createCheckIsReadonlyStepField(
        readonly,
        enabledFields
      );

      return (
        <Form layout="vertical">
          <FormGroupOfTwoItems>
            <Form.Item
              label={t(`wizard.steps.IdealTenant.form.min_age.title`)}
              validateStatus={errors?.min_age ? "error" : ""}
              help={
                errors?.min_age
                  ? t("wizard.steps.IdealTenant.form.min_age.error")
                  : ""
              }
            >
              {viewonly ? (
                <StepViewOnlyValue>{values.min_age ?? ""}</StepViewOnlyValue>
              ) : (
                <Input
                  size={inputSize}
                  disabled={isReadonly("min_age")}
                  value={values.min_age ?? undefined}
                  placeholder={t(
                    `wizard.steps.IdealTenant.form.min_age.placeholder`
                  )}
                  type="number"
                  onChange={(e) => {
                    setValues({
                      ...values,
                      min_age: e.target.value
                    });
                  }}
                />
              )}
            </Form.Item>
            <Form.Item
              label={t(`wizard.steps.IdealTenant.form.max_age.title`)}
              validateStatus={errors?.max_age ? "error" : ""}
              help={
                errors?.max_age
                  ? t("wizard.steps.IdealTenant.form.max_age.error")
                  : ""
              }
            >
              {viewonly ? (
                <StepViewOnlyValue>{values.max_age ?? ""}</StepViewOnlyValue>
              ) : (
                <Input
                  size={inputSize}
                  disabled={isReadonly("max_age")}
                  value={values.max_age ?? undefined}
                  placeholder={t(
                    `wizard.steps.IdealTenant.form.max_age.placeholder`
                  )}
                  type="number"
                  onChange={(e) => {
                    setValues({
                      ...values,
                      max_age: e.target.value
                    });
                  }}
                />
              )}
            </Form.Item>
          </FormGroupOfTwoItems>
          <Form.Item
            label={t(`wizard.steps.IdealTenant.form.gender.title`)}
            validateStatus={errors?.gender ? "error" : ""}
            help={
              errors?.gender
                ? t("wizard.steps.IdealTenant.form.gender.error")
                : ""
            }
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {values.gender
                  ? values.gender === "any"
                    ? t("wizard.steps.IdealTenant.form.gender.noPreference")
                    : values.gender
                  : ""}
              </StepViewOnlyValue>
            ) : (
              <RadioGroup
                disabled={isReadonly("gender")}
                value={values.gender}
                options={genderOptions}
                onChange={(v) => {
                  setValues({
                    ...values,
                    gender: v.target.value
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.IdealTenant.form.spoken_languages.title`)}
            validateStatus={errors?.tenant_spoken_languages ? "error" : ""}
            help={
              errors?.tenant_spoken_languages
                ? t("wizard.errors.selection")
                : ""
            }
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {allLanguagesOptions
                  .filter((c) =>
                    values.tenant_spoken_languages?.includes(c.value as string)
                  )
                  ?.map((o) => o.label)
                  .join(", ") || "ANY"}
              </StepViewOnlyValue>
            ) : (
              <Select
                size={inputSize}
                mode="multiple"
                disabled={isReadonly("tenant_spoken_languages")}
                value={values.tenant_spoken_languages ?? undefined}
                placeholder={t(
                  `wizard.steps.IdealTenant.form.spoken_languages.placeholder`
                )}
                defaultValue={["ANY"]}
                options={allLanguagesOptions}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(newValue) => {
                  // If the newValue contains "ANY" and other languages, filter out "ANY"
                  if (newValue.includes("ANY") && newValue.length > 1) {
                    newValue = newValue.filter((val) => val !== "ANY");
                  }

                  // If newValue is empty, add "ANY"
                  if (newValue.length === 0) {
                    newValue = ["ANY"];
                  }

                  setValues({ ...values, tenant_spoken_languages: newValue });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(
              `wizard.steps.IdealTenant.form.tenant_characteristics.title`
            )}
            validateStatus={errors?.tenant_characteristics ? "error" : ""}
            help={
              errors?.tenant_characteristics ? t("wizard.errors.selection") : ""
            }
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {characteristicsOptions
                  .filter((c) =>
                    values.tenant_characteristics?.includes(c.value)
                  )
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </StepViewOnlyValue>
            ) : (
              <WizardButtonSelect
                disabled={isReadonly("tenant_characteristics")}
                value={values.tenant_characteristics ?? undefined}
                options={characteristicsOptions}
                onChange={(newValue) =>
                  setValues({
                    ...values,
                    tenant_characteristics: newValue
                  })
                }
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.IdealTenant.form.tenant_description.title`)}
            validateStatus={errors?.tenant_description ? "error" : ""}
            help={
              errors?.tenant_description ? t("wizard.errors.selection") : ""
            }
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {descriptionOptions
                  .filter((c) => values.tenant_description?.includes(c.value))
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </StepViewOnlyValue>
            ) : (
              <WizardButtonSelect
                disabled={isReadonly("tenant_description")}
                value={values.tenant_description ?? undefined}
                options={descriptionOptions}
                onChange={(newValue) =>
                  setValues({
                    ...values,
                    tenant_description: newValue
                  })
                }
              />
            )}
          </Form.Item>
        </Form>
      );
    }
  );
